import type {
  DocumentContent,
  DocumentParagraph,
  DocumentSentence,
  EditableDocumentContent,
  DocumentBlock
} from '@/types/documents'

/**
 * Split text into paragraphs and sentences
 * - Paragraphs are split by double newlines (\n\n or \r\n\r\n)
 * - Sentences are split by Chinese punctuation: 。！？
 */
export function processText(text: string): DocumentContent {
  // Normalize line endings
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

  // Split into paragraphs (by double newlines or more)
  const paragraphTexts = normalized
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0)

  const paragraphs: DocumentParagraph[] = paragraphTexts.map((paraText) => {
    // Preserve single line breaks within paragraphs by replacing them with a marker
    // We'll restore them when displaying
    const lineBreakMarker = '\u0001' // Use a control character as marker
    const textWithMarkers = paraText.replace(/\n/g, lineBreakMarker)

    // Split into sentences by Chinese punctuation: 。！？
    // Also handle English punctuation followed by space/newline for mixed content
    const sentenceTexts = textWithMarkers
      .split(/([。！？]|\.\s+|!\s+|\?\s+)/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      // Recombine punctuation with previous sentence
      .reduce((acc, curr, idx) => {
        if (idx === 0) {
          return [curr]
        }
        // If current is just punctuation, attach to previous
        if (/^[。！？\.!?]+$/.test(curr)) {
          acc[acc.length - 1] += curr
          return acc
        }
        // Check if previous ended with punctuation
        const prev = acc[acc.length - 1]
        if (/[。！？\.!?]$/.test(prev)) {
          acc[acc.length - 1] += ' ' + curr
          return acc
        }
        // Otherwise, new sentence
        acc.push(curr)
        return acc
      }, [] as string[])

    // Restore line breaks in sentences
    const sentences: DocumentSentence[] = sentenceTexts.map((sentenceText) => ({
      text: sentenceText.replace(new RegExp(lineBreakMarker, 'g'), '\n').trim()
    }))

    return { sentences }
  })

  // If no paragraphs were created (single block of text), create one paragraph
  if (paragraphs.length === 0) {
    return {
      paragraphs: [
        {
          sentences: [{ text: normalized.trim() }]
        }
      ]
    }
  }

  return { paragraphs }
}

/**
 * Clean up text by removing excessive whitespace
 */
export function cleanText(text: string): string {
  return text
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/\n{3,}/g, '\n\n') // Replace 3+ newlines with double newline
    .trim()
}

/**
 * Convert editable document content back to standard DocumentContent format
 * Only includes blocks that are selected
 * @param editableContent - The editable content with blocks
 * @returns Standard DocumentContent with all selected blocks combined
 */
export function convertEditableToDocumentContent(
  editableContent: EditableDocumentContent
): DocumentContent {
  const selectedBlocks = editableContent.blocks.filter(
    (block) => block.selected
  )

  if (selectedBlocks.length === 0) {
    return { paragraphs: [] }
  }

  // Combine all selected blocks' paragraphs
  // Filter out empty paragraphs and sentences to ensure valid structure
  const allParagraphs: DocumentParagraph[] = []
  for (const block of selectedBlocks) {
    for (const para of block.content.paragraphs) {
      // Only include paragraphs that have at least one non-empty sentence
      const validSentences = para.sentences.filter(
        (sent) => sent.text && sent.text.trim().length > 0
      )
      if (validSentences.length > 0) {
        allParagraphs.push({ sentences: validSentences })
      }
    }
  }

  // Ensure we always return a valid structure, even if empty
  return { paragraphs: allParagraphs }
}

/**
 * Convert text input into editable document content with blocks
 * Splits text by double newlines to create separate blocks
 * @param text - The input text
 * @returns EditableDocumentContent with text blocks
 */
export function convertTextToEditableContent(
  text: string
): EditableDocumentContent {
  // Normalize line endings
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

  // Split into blocks by double newlines
  const blockTexts = normalized
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter((b) => b.length > 0)

  // If no blocks created, create one from the entire text
  const blocks: DocumentBlock[] =
    blockTexts.length > 0
      ? blockTexts.map((blockText, index) => ({
          id: `block-${index + 1}`,
          type: 'block',
          content: processText(blockText),
          selected: true
        }))
      : [
          {
            id: 'block-1',
            type: 'block',
            content: processText(normalized.trim()),
            selected: true
          }
        ]

  return {
    blocks,
    sourceType: 'text'
  }
}

/**
 * Convert PDF pages into editable document content
 * @param pages - Array of page contents with page numbers
 * @returns EditableDocumentContent with page blocks
 */
export function convertPdfPagesToEditableContent(
  pages: Array<{ pageNumber: number; content: DocumentContent }>
): EditableDocumentContent {
  const blocks: DocumentBlock[] = pages.map((page) => ({
    id: `page-${page.pageNumber}`,
    type: 'page',
    pageNumber: page.pageNumber,
    content: page.content,
    selected: true
  }))

  return {
    blocks,
    sourceType: 'pdf'
  }
}
